/**
 * Format a USD cost value for display in the chat caption, session total,
 * and admin dashboard.
 *
 * Per spec D5:
 *   - Round to 6 decimals max so floating-point dust like 0.1+0.2 doesn't leak.
 *   - Trim trailing zeros so we don't pad with meaningless precision.
 *   - But preserve minimum precision so sub-cent values stay readable:
 *       abs >= $1   → minimum 2 decimals (standard currency style)
 *       abs <  $1   → minimum 4 decimals (so $0.005 renders as $0.0050,
 *                                          and $0 renders as $0.0000)
 *
 * Returns null when value is null/undefined/non-finite so callers can skip
 * rendering the dollar field entirely.
 *
 * Spec examples (D5):
 *   0.0053       → "$0.0053"     (4 decimals, no trim needed)
 *   0.000123     → "$0.000123"   (6 decimals, no trim needed)
 *   1.23         → "$1.23"       (>= $1 → 2-decimal floor)
 *   0            → "$0.0000"     (< $1 → 4-decimal floor)
 *   1.0          → "$1.00"       (>= $1 → 2-decimal floor)
 *   0.5          → "$0.5000"     (< $1 → 4-decimal floor)
 *   null         → null          (host renders without the dollar slot)
 */
export function formatCostUsd(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (!Number.isFinite(value)) return null;

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const rounded = Math.round(abs * 1_000_000) / 1_000_000;
  const minDecimals = abs >= 1 ? 2 : 4;

  // Start at 6 decimals, then trim trailing zeros down to but not below the
  // minimum. Keeps 1.23 as "1.23" and 0.5 as "0.5000".
  const sixDecimal = rounded.toFixed(6);
  let trimmed = sixDecimal;
  while (trimmed.endsWith('0')) {
    const dotIndex = trimmed.indexOf('.');
    if (dotIndex < 0) break;
    const decimalCount = trimmed.length - dotIndex - 1;
    if (decimalCount <= minDecimals) break;
    trimmed = trimmed.slice(0, -1);
  }

  return `$${sign}${trimmed}`;
}
